/**
 * MayaLintDialog — Pré-validação proativa do XML pela Maya antes de transmitir.
 * Aponta warnings (alta/média/baixa), checklist do que está OK,
 * e oferece botão para transmitir mesmo assim ou cancelar para correção.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  CheckCircle2, AlertCircle, AlertTriangle, ShieldAlert,
  RefreshCw, Loader2, Send,
} from 'lucide-react';
import MayaAvatar from '../maya/MayaAvatar';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const SEV_META = {
  alta:  {
    badge: 'bg-red-600 text-white',
    bg: 'bg-red-50 border-red-200',
    icon: ShieldAlert,
    iconColor: 'text-red-600',
    label: 'Alta',
  },
  media: {
    badge: 'bg-amber-500 text-white',
    bg: 'bg-amber-50 border-amber-200',
    icon: AlertTriangle,
    iconColor: 'text-amber-600',
    label: 'Média',
  },
  baixa: {
    badge: 'bg-slate-400 text-white',
    bg: 'bg-slate-50 border-slate-200',
    icon: AlertCircle,
    iconColor: 'text-slate-500',
    label: 'Baixa',
  },
};

const MayaLintDialog = ({
  open,
  onOpenChange,
  eventoId,
  onConfirmTransmit,    // (eventoId) => fluxo de assinar+enviar real
  autoRun = true,        // executa lint automaticamente quando abre
}) => {
  const [loading, setLoading] = useState(false);
  const [lint, setLint] = useState(null);

  const carregar = async () => {
    if (!eventoId) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/esocial/eventos/${eventoId}/maya-lint`, auth(),
      );
      setLint(r.data?.lint || null);
    } finally {
      setLoading(false);
    }
  };

  const executarLint = async (force = false) => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/api/esocial/eventos/${eventoId}/maya-lint${force ? '?force=true' : ''}`,
        {}, { ...auth(), timeout: 90000 },
      );
      setLint(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Maya falhou no lint');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && eventoId) {
      (async () => {
        await carregar();
        if (autoRun) executarLint(false);
      })();
    }
  }, [open, eventoId, autoRun]);  // eslint-disable-line

  const altas = lint?.contagens?.alta || 0;
  const medias = lint?.contagens?.media || 0;
  const baixas = lint?.contagens?.baixa || 0;
  const totalProblems = altas + medias + baixas;
  const okToTransmit = !!lint?.ok_para_transmitir && altas === 0;

  const headerVariant = loading
    ? 'thinking'
    : !lint
      ? 'happy'
      : altas > 0
        ? 'alert'
        : medias > 0
          ? 'alert'
          : 'celebrating';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <MayaAvatar variant={headerVariant} size="md" showStatusDot pulse={loading}
                        data-testid="maya-avatar-lint-header" />
            <div className="flex flex-col">
              <span>Maya — Pré-validação do XML</span>
              <span className="text-xs font-normal text-slate-500">
                Antes de transmitir à gov.br
              </span>
            </div>
          </DialogTitle>
          <DialogDescription>
            A Maya analisa o XML que será enviado e aponta problemas prováveis antes de você gastar protocolo no gov.br.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-sm text-slate-600">
            <MayaAvatar variant="thinking" size="lg" pulse showStatusDot
                        data-testid="maya-avatar-lint-loading" />
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Maya está validando o XML…
            </span>
          </div>
        )}

        {!loading && lint && (
          <div className="space-y-4">
            {/* Resumo */}
            <div className={`rounded-md border p-3 flex items-start gap-3 ${
              okToTransmit
                ? 'bg-emerald-50 border-emerald-200'
                : altas > 0
                  ? 'bg-red-50 border-red-200'
                  : 'bg-amber-50 border-amber-200'
            }`}>
              <MayaAvatar variant={headerVariant} size="sm" data-testid="maya-avatar-summary" />
              <div className="flex-1">
                <div className="font-semibold text-sm text-slate-800">
                  {okToTransmit
                    ? 'Tudo pronto para transmitir!'
                    : altas > 0
                      ? `${altas} problema(s) crítico(s) detectado(s)`
                      : `${totalProblems} ponto(s) de atenção`}
                </div>
                <p className="text-xs text-slate-600 mt-1">{lint.resumo}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                {altas > 0 && <Badge className="bg-red-600 text-white">{altas} alta</Badge>}
                {medias > 0 && <Badge className="bg-amber-500 text-white">{medias} média</Badge>}
                {baixas > 0 && <Badge className="bg-slate-400 text-white">{baixas} baixa</Badge>}
              </div>
            </div>

            {/* Warnings */}
            {lint.warnings?.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                  Problemas detectados ({lint.warnings.length})
                </div>
                <div className="space-y-2">
                  {lint.warnings.map((w, i) => {
                    const meta = SEV_META[(w.severidade || 'media').toLowerCase()] || SEV_META.media;
                    const Icon = meta.icon;
                    return (
                      <div
                        key={i}
                        className={`rounded-md border p-3 ${meta.bg} space-y-1.5`}
                        data-testid={`maya-lint-warning-${i}`}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <Icon className={`h-4 w-4 ${meta.iconColor}`} />
                          <Badge className={`${meta.badge} text-[10px]`}>{meta.label}</Badge>
                          {w.codigo && (
                            <code className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                              {w.codigo}
                            </code>
                          )}
                          <span className="text-sm font-semibold text-slate-800">{w.titulo}</span>
                        </div>
                        {w.descricao && (
                          <p className="text-xs text-slate-600 leading-relaxed pl-6">{w.descricao}</p>
                        )}
                        {(w.campo || w.valor_atual || w.valor_sugerido) && (
                          <div className="pl-6 pt-1 text-[11px] flex flex-wrap items-center gap-1.5 border-t border-current/10">
                            {w.campo && (
                              <code className="bg-white px-1.5 py-0.5 rounded border text-slate-700">
                                {w.campo}
                              </code>
                            )}
                            {w.valor_atual && (
                              <>
                                <span className="text-slate-400">atual:</span>
                                <code className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                                  {w.valor_atual}
                                </code>
                              </>
                            )}
                            {w.valor_sugerido && (
                              <>
                                <span className="text-slate-400">→</span>
                                <code className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                                  {w.valor_sugerido}
                                </code>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Checklist OK */}
            {lint.checklist_passed?.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2">
                  ✓ Tudo certo nestes pontos ({lint.checklist_passed.length})
                </div>
                <ul className="space-y-1">
                  {lint.checklist_passed.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="text-[11px] text-slate-400 text-right pt-1">
              Validado em {lint.checado_em ? new Date(lint.checado_em).toLocaleString('pt-BR') : '-'} ·
              modelo {lint.modelo}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          {lint && (
            <Button variant="outline" onClick={() => executarLint(true)} disabled={loading}
                    data-testid="maya-lint-refresh-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Revalidar
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="maya-lint-cancel-btn">
            Cancelar
          </Button>
          {onConfirmTransmit && lint && (
            <Button
              onClick={() => onConfirmTransmit(eventoId, lint)}
              disabled={loading}
              data-testid="maya-lint-confirm-transmit-btn"
              className={
                okToTransmit
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : altas > 0
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-amber-500 hover:bg-amber-600'
              }
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {okToTransmit
                ? 'Transmitir agora'
                : altas > 0
                  ? 'Transmitir mesmo assim'
                  : 'Transmitir com atenção'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MayaLintDialog;
